import {
  CurecallTimelineLine,
  CurecallTimelineMessageItem,
  ECurecallTimelineMessageItemAppearance,
  CurecallConfirmationModal,
  useClickOutside,
} from '@honestica/curecall-ui'
import { TTimelineChatbotElement } from './followup-timeline.type'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useParams } from 'react-router-dom'
import { useState, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChatbotService } from 'src/services/chatbot.service'
import { useNotification } from 'src/providers/notification.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useSession } from 'src/providers/auth.provider'
import { config } from 'src/config'
import { getErrorMessage } from 'src/utils/http.util'
import { getRelativeSendingDate } from '../messages/_components/programmed-message.helper'
import { twMerge } from 'src/utils/tw-merge.util'
import { DuplicateChatbotModal } from './duplicate-chatbot-modal.component'
import { TimelineChatbotItemTooltip } from './timeline-chatbot-item-tooltip.component'
import { TimelineChatbotItemMenu } from './timeline-chatbot-item-menu.component'

const TimelineChatbotItem = ({
  timelineElement,
  correspondingMedEventName,
}: {
  timelineElement: TTimelineChatbotElement
  correspondingMedEventName: string
}) => {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const params = useParams()
  const projectId = params.projectId as string
  const followupId = params.followupId as string
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const { project } = useCurrentProject()
  const { currentUser } = useSession()

  useClickOutside({ ref: containerRef, callBack: () => setIsMenuOpen(false) })

  const backofficeUrl =
    currentUser?.isAdmin && config.BACKOFFICE_URL
      ? `${config.BACKOFFICE_URL}/projects/${project.id}/workflows/${timelineElement.id}`
      : null

  const deleteMutation = useMutation({
    mutationFn: () => ChatbotService.delete(Number(projectId), Number(followupId), timelineElement.id),
    onSuccess: () => {
      notify({
        title: t('pages.configuration.followups.chatbot.delete.success'),
        message: t('pages.configuration.followups.chatbot.delete.success'),
        appearance: 'SUCCESS',
      })
      queryClient.invalidateQueries({
        queryKey: ['followup-self-configuration', Number(projectId), followupId],
      })
      setIsDeleteModalOpen(false)
    },
    onError: (error) => {
      notify({
        title: t('common.error.generic'),
        message: getErrorMessage(error),
        appearance: 'ERROR',
      })
      setIsDeleteModalOpen(false)
    },
  })

  return (
    <>
      <div ref={containerRef} className={twMerge('c-relative c-max-w-full', !isMenuOpen && 'c-group')}>
        <CurecallTimelineMessageItem
          leftLabel={getRelativeSendingDate(timelineElement.schedule.durationDiff)}
          className="tw-relative c-cursor-pointer"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          onDoubleClick={
            backofficeUrl
              ? () => {
                  setIsMenuOpen(false)
                  window.open(backofficeUrl, '_blank', 'noopener,noreferrer')
                }
              : undefined
          }
          appearance={
            timelineElement.isActive
              ? ECurecallTimelineMessageItemAppearance.DEFAULT
              : ECurecallTimelineMessageItemAppearance.SECONDARY
          }
        >
          {`${timelineElement.name} (${
            timelineElement.isActive
              ? t('pages.configuration.followups.chatbot.published')
              : t('pages.configuration.followups.chatbot.unpublished')
          })`}
        </CurecallTimelineMessageItem>

        <TimelineChatbotItemTooltip
          timelineElement={timelineElement}
          correspondingMedEventName={correspondingMedEventName}
        />

        {isMenuOpen && (
          <TimelineChatbotItemMenu
            onEditProgrammation={() => {
              navigate(
                `/${projectId}/configuration/followups/${followupId}/chatbot/${timelineElement.id}/update-general-form`,
              )
              setIsMenuOpen(false)
            }}
            onEditNodes={() => {
              navigate(
                `/${projectId}/configuration/followups/${followupId}/chatbot/${timelineElement.id}/update-nodes`,
              )
              setIsMenuOpen(false)
            }}
            onDuplicate={() => {
              setIsDuplicateModalOpen(true)
              setIsMenuOpen(false)
            }}
            onDelete={() => {
              setIsDeleteModalOpen(true)
              setIsMenuOpen(false)
            }}
          />
        )}
      </div>
      <CurecallTimelineLine />

      <CurecallConfirmationModal
        title={t('pages.configuration.followups.chatbot.delete.confirmation-title')}
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
        cancelButtonLabel={t('common.actions.cancel')}
        confirmButtonLabel={t('common.actions.delete')}
        color="ALERT"
      >
        <div className="c-text-base">
          {t('pages.configuration.followups.chatbot.delete.confirmation-message', {
            name: timelineElement.name,
          })}
        </div>
      </CurecallConfirmationModal>
      <DuplicateChatbotModal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        chatbot={{
          id: timelineElement.id,
          name: timelineElement.name,
        }}
      />
    </>
  )
}

export { TimelineChatbotItem }
